import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { TValuesTimeseriesPoints } from 'App/Map/types'
import type { StackedChartSection } from 'components/StackedChart/types'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type {
  TPointCategory,
  TSelectedFieldAssetsStoreState,
} from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedPointHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { chooseAmongstUnAggHourlyAndDaily } from '../../chooseAmongstUnAggHourlyAndDaily'
import { getFavoriteDepths } from './_utils/getFavoriteDepths'
import { awcHeatmap } from './awcHeatmap'
import { awcLineAndWaterEvents } from './awcLineAndWaterEvents'
import { ionicContent } from './ionicContent'
import { soilTemperature } from './soilTemperature'

const soilPointCategory: TPointCategory = 'soilPoint'
const precipitationPointCategory: TPointCategory = 'outOfBlockPoint'

const valueTypes: TValuesTimeseriesPoints[] = [
  'soilMoisture',
  'soilTemperature',
  'soilIonicContent',
  'precipitation',
]

export type SoilValueType = VV.DomainTypes.Soil.TSoil | VV.DomainTypes.Soil.TSoilAggregated

export const checkPermission = () =>
  selectedPropertyHasPermission({ permission: 'VIEW_SOIL_MOISTURE_DETAILS' })

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.soil || !selectedFieldAssets[soilPointCategory]) return {}

  const preferredAggregationIntervalPrecipitation = {
    preferredAggregationInterval: detailsPanelStore.getState().precipitationAggregation as 'DAILY' | 'HOURLY',
  }

  const preferredAggregationInterval = {
    preferredAggregationInterval: chooseAmongstUnAggHourlyAndDaily(),
  }

  const valuesRequested = Object.fromEntries(
    valueTypes.map((valueType) => {
      if (valueType === 'precipitation') {
        return [valueType, preferredAggregationIntervalPrecipitation]
      }

      return [valueType, preferredAggregationInterval]
    }),
  )

  if (
    !doesSelectedPointHaveValueTypes({
      valuesTimeseries: valueTypes,
      pointCategory: soilPointCategory,
    })
  )
    return {}

  const uniqueLngLatsToRequest = [selectedFieldAssets[soilPointCategory]]

  if (selectedFieldAssets[precipitationPointCategory]) {
    uniqueLngLatsToRequest.push(selectedFieldAssets[precipitationPointCategory])
  }

  return {
    points: {
      lngLats: uniqueLngLatsToRequest,
      valuesRequested,
    },
  }
}

// TODO: At the moment we are only making line series with the average value. We are not yet making area series out of the min/max values.
// TODO: We should probably have selectedDepths as checkboxes (outside of Highcharts) instead of as a Highcharts legend
export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection[] => {
  const soilStationLngLat = selectedFieldAssetsStore.getState()[soilPointCategory]
  const precipitationStationLngLat = selectedFieldAssetsStore.getState()[precipitationPointCategory]
  const soilCharts = []
  const soilStationFavoriteDepths = getFavoriteDepths()

  soilCharts.push(
    awcLineAndWaterEvents({
      data,
      precipitationStationLngLat,
      soilStationLngLat,
      compareSeasonsData,
      soilStationFavoriteDepths,
    }),
  )

  soilCharts.push(awcHeatmap({ data, soilStationLngLat }))

  soilCharts.push(ionicContent({ data, soilStationLngLat }))

  soilCharts.push(soilTemperature({ data, soilStationLngLat, compareSeasonsData, soilStationFavoriteDepths }))

  return soilCharts
}
